<template>
  <v-card>
    <div class="header">
      <div class="d-flex align-center justify-space-between">
        <h2>
          {{
            form === 0
              ? 'Permintaan Pemeriksaan Laboratorium'
              : 'Detail Permintaan Pemeriksaan Laboratorium'
          }}
        </h2>
        <v-btn text fab small dense depressed @click="$emit('close-dialog')"
          ><v-icon>mdi-close</v-icon></v-btn
        >
      </div>
      <div class="flex-wrapper mt-1">
        <span>No RM</span>
        <span>{{ patientData.rmNumber }}</span>
        <span>Nama</span>
        <span>
          {{ patientData.name }} ({{ patientData.gender ? 'L' : 'P' }})
        </span>
        <span>Tanggal Lahir</span>
        <span>{{ patientData.birthDate }} ({{ patientData.age }})</span>
        <span>Petugas</span>
        <span>{{ staffUnit }}</span>
        <span>Unit</span>
        <span class="text-capitalize">{{ patientData.unit }}</span>
      </div>
    </div>
    <v-divider class="mb-2 mt-2" />
    <div class="main">
      <order-examination-tab
        v-if="form === 0"
        ref="order"
        :state="
          routeSplit[1].toLowerCase() === 'pendaftaran' &&
          routeSplit[2].toLowerCase() === 'laboratorium'
            ? 'register'
            : ''
        "
      />
      <detail-examination-tab
        :is-new-order="isNewOrder"
        v-if="form === 1"
        is-from-history
        :status-order="{
          color: '',
          text: 'belum diproses',
        }"
      />
    </div>
    <div class="footer py-4">
      <v-btn
        @click="backButton.action"
        class="text-capitalize mr-3"
        style="letter-spacing: .01px;"
        color="primary"
        text
        dense
        single-line
        >{{ backButton.text }}</v-btn
      >
      <v-btn
        :loading="isLoading"
        class="text-capitalize"
        @click="confirmButton.action"
        depressed
        dense
        single-line
        :disabled="isDisableConfirmBtn"
        style="letter-spacing: .01px;"
        color="primary"
        >{{ confirmButton.text }}</v-btn
      >
    </div>
  </v-card>
</template>

<script>
import OrderExaminationTab from './OrderExaminationTab.vue';
import DetailExaminationTab from './DetailExaminationTab.vue';
import { createNamespacedHelpers } from 'vuex';

const {
  mapGetters: patientGetters,
  mapMutations: patientMutations,
} = createNamespacedHelpers('PatientRegistration');
const {
  mapGetters: labGetters,
  mapActions: labActions,
  mapMutations: labMutations,
} = createNamespacedHelpers('ExaminationLaboratory');

export default {
  components: { OrderExaminationTab, DetailExaminationTab },
  name: 'NewOrderFromHistory',
  props: {
    isNewOrder: Boolean,
    patientData: Object,
  },
  data() {
    return {
      isLoading: false,
      form: 0,
    };
  },
  beforeDestroy() {
    this.clearState();
  },
  computed: {
    ...labGetters(['getIsRegister', 'getInputOrderCheckedItems']),
    ...patientGetters(['getPatientRegistration', 'getIsRegistration']),
    routeSplit() {
      return this.$route.path.split('/');
    },
    staffUnit() {
      return this.$store.getters.userLoggedIn.unit.some(
        arr => arr.name.toLowerCase() === 'laboratorium',
      )
        ? this.$store.getters.userLoggedIn.name
        : '-';
    },
    isDisableConfirmBtn() {
      return this.form === 0 && this.getInputOrderCheckedItems.length === 0;
    },
    patientRegistration() {
      return function(key) {
        return this.getPatientRegistration[key];
      };
    },
    backButton: {
      get() {
        return {
          text: this.form === 0 ? 'Batal' : 'Kembali',
          action: () => {
            if (this.form === 0) {
              this.$emit('close-dialog');
            } else {
              this.form = 0;
            }
          },
        };
      },
    },
    confirmButton: {
      get() {
        return {
          text: this.form === 0 ? 'Lanjut' : 'Order',
          action: () => {
            if (this.form === 0) {
              if (this.$refs.order.$refs.form.validate()) {
                this.form = 1;
              }
            } else {
              if (this.getIsRegister) {
                this.handlePostRegistration();
              } else {
                this.handlePostExaminationRequest();
              }
            }
          },
        };
      },
    },
  },
  methods: {
    ...labMutations(['clearState']),
    ...patientMutations(['setPatientRegistration']),
    ...labActions([
      'resolvePostOrderExamination',
      'resolveUpdateLabRequest',
      'resolveConfirmLabRequest',
      'resolvePostLabResult',
      'resolvePostLabRegistration',
    ]),
    async handlePostExaminationRequest() {
      this.isLoading = true;
      await this.resolvePostOrderExamination().then(arr => {
        if (arr) {
          this.$store.commit('changeSelectedPatient', {
            ...this.$store.getters.selectedPatient,
            noRegist: arr.registration_number,
          });
          this.setPatientRegistration({
            label: 'idEmr',
            value: arr?.id_emr,
          });
        }
        this.isLoading = false;
      });
      this.$emit('close-dialog');
    },
    async handlePostRegistration() {
      this.isLoading = true;
      const patientData = {
        id_emr: this.patientRegistration('idEmr'),
        status: {
          isNew: this.patientRegistration('statusIsNew'),
          type: this.patientRegistration('statusType'),
        },
        meta: {
          name: this.patientRegistration('name'),
          nickname: this.patientRegistration('nickname'),
          phone_number: this.patientRegistration('phoneNumber'),
          gender: this.patientRegistration('gender'),
          birth: {
            date: this.patientRegistration('birthDate'),
            place: this.patientRegistration('birthPlace'),
          },
          address: {
            district: this.patientRegistration('addressDistrict')?.name || '',
            province: this.patientRegistration('addressProvince')?.name || '',
            sub_district:
              this.patientRegistration('addressSubDistrict')?.name || '',
            text: this.patientRegistration('addressText'),
            city: this.patientRegistration('addressCity')?.name || '',
          },
          identity: {
            type: this.patientRegistration('identityType'),
            number: this.patientRegistration('identityNumber'),
          },
          religion: this.patientRegistration('religion'),
          blood_type: this.patientRegistration('bloodType'),
          education: this.patientRegistration('education'),
          ethnicity: this.patientRegistration('ethnicity'),
          profession: this.patientRegistration('profession'),
          martial_status: this.patientRegistration('martialStatus'),
          father: this.patientRegistration('father'),
          mother: this.patientRegistration('mother'),
          disabilities: this.patientRegistration('disabilities'),
          communication_obstacles: this.patientRegistration(
            'communicationObstacles',
          ),
          language_mastering: this.patientRegistration('languageMastering'),
          assurance: {
            type: this.patientRegistration('assuranceType'),
            name: this.patientRegistration('assuranceName'),
            number: this.patientRegistration('assuranceNumber'),
            isProlanis: this.patientRegistration('assuranceIsProlanis'),
          },
        },
      };
      await this.resolvePostLabRegistration(patientData).then(arr => {
        this.$store.commit('changeSelectedPatient', {
          ...this.$store.getters.selectedPatient,
          noRegist: arr.registration_number,
        });
        this.setPatientRegistration({
          label: 'idEmr',
          value: arr?.id_emr,
        });
        this.isLoading = false;
      });
      this.$emit('close-dialog');
    },
  },
};
</script>

<style lang="scss" scoped>
.v-card {
  width: 80vw;

  .footer {
    padding: 0.3vw 1.7vw;
    display: flex;
    justify-content: flex-end;
  }

  .main {
    padding: 0.3vw 1.7vw 0 1.7vw;
  }

  .header {
    padding: 0.8vw 1.7vw 0 1.7vw;

    h2 {
      font-size: 18px;
      text-align: left;
      color: #404040;
    }
    p:nth-of-type(even) {
      margin-right: 2rem;
    }
    p:nth-of-type(odd) {
      margin-right: 1.8rem;
      color: #9e9e9e;
    }
  }
}
.flex-wrapper {
  display: flex;
  font-size: 13px;
  font-weight: 400;
}

.flex-wrapper > *:not(:last-child) {
  margin-right: 0.75rem;
  color: #9e9e9e;
}

.flex-wrapper > *:nth-child(even):not(:last-child) {
  margin-right: 1.25rem;
  color: #404040;
}
</style>
